<template>
	<div class="table-overflow-hide">
		<div class="directoryTable scrollable-55vh">
			<table class="table">
				<thead class="sticky-header">
					<tr>
						<th class="icon-col"></th>
						<SortHeader
							title="name"
							:asc="currentSort.asc"
							:sort-key="currentSort.key"
							@click="() => setSort('name')"
							data-cy="report-table-header"
							class="header-cell"
							color="#695b59"
							weight="500"
						></SortHeader>
						<th>team members</th>
						<th class="icon-col">
							<v-btn
								@click="teamsS.teams.reload()"
								icon
								size="x-small"
								color="gray200"
								:loading="teamsS.teams.loading"
								style="max-width: 20px; max-height: 20px"
							>
								<v-icon>mdi-reload</v-icon></v-btn
							>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="row in paginatedResults"
						:key="row.uid"
						:class="{
							'table-row': true,
							selected:
								(editModal.show && editModal.team === row) ||
								(editRelationModal.show && editRelationModal.team === row),
						}"
					>
						<td>
							<v-btn
								color="gray300"
								density="compact"
								icon
								variant="text"
								@click="updatePinTeam(row)"
								:loading="state.loadPinned.includes(row.uid)"
								:disabled="deleted || !canEditDirectory"
							>
								<v-tooltip location="bottom" class="custom-tooltip" activator="parent">
									Lock Record
								</v-tooltip>
								<v-icon :color="row.pinned ? 'primary' : 'gray500'"
									>mdi-lock-outline</v-icon
								>
							</v-btn>
						</td>
						<td data-cy="table-col-name">
							{{ row.team_name }}
							<v-btn
								@click="
									editModal.show = true;
									editModal.team = row;
								"
								color="gray300"
								density="compact"
								icon
								variant="text"
								:disabled="!canEditDirectory"
							>
								<v-icon color="gray500">mdi-note-edit</v-icon>
							</v-btn>
						</td>
						<td data-cy="table-col-members">
							{{ row.teacher_description }}
						</td>
						<td tabindex="1">
							<!-- <v-tooltip
                v-if="schoolStore.schoolDetails.data?.integration == 'none'"
                activator="parent"
                location="bottom"
                >teams don't have relations when integration is set to none</v-tooltip
              > -->
							<v-btn
								@click="
									editRelationModal.show = true;
									editRelationModal.team = row;
								"
								color="gray300"
								density="compact"
								icon
								variant="text"
								:disabled="deleted || !canEditDirectory"
							>
								<v-icon color="gray500">mdi-account-group</v-icon>
							</v-btn>
						</td>
					</tr>
				</tbody>
			</table>

			<div
				v-if="teamsS.teams.loading && !teamsS.teams.data.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<LoadingState text="Loading Teams"></LoadingState>
			</div>
			<div
				v-if="!teamsS.teams.loading && !teamsS.teams.data.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<div class="empty-state">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">We couldn't find any Teams.</div>
					<p>Try reloading the page or adding a new team.</p>
				</div>
			</div>
		</div>
	</div>
	<div class="table-pagination-btns">
		<pagination v-bind="forComponent()"></pagination>
		<div class="table-buttons" v-if="canEditDirectory">
			<v-btn @click="addModal.show = true" data-cy="directory-team-create-btn">Create New</v-btn>
			<v-btn
				@click="uploadModal.show = true"
				color="#E5E4DB"
				append-icon="mdi-tray-arrow-up"
			>
				Upload
			</v-btn>

			<div class="d-flex align-center">
				<v-btn
					@click="download"
					:loading="state.loadDownload"
					color="#E5E4DB"
					append-icon="mdi-tray-arrow-down"
					>Download</v-btn
				>
				<div class="ml-1" tabindex="1">
					<v-tooltip location="bottom" activator="parent">Download Relations</v-tooltip>
					<RelationsDownload :download-types="['team_enrollment']"></RelationsDownload>
				</div>
			</div>
		</div>
	</div>
	<edit-team-modal v-model:show="editModal.show" :team="editModal.team"></edit-team-modal>
	<add-team-modal
		v-model:show="addModal.show"
		@update:search="
			$emit('update:search', $event);
			addModal.show = false;
		"
	></add-team-modal>

	<team-relations-modal
		v-model:show="editRelationModal.show"
		:directory-entity="editRelationModal.team"
		:entity-type="'team'"
		:entity-id="editRelationModal.team.uid"
		:entity-name="`${editRelationModal.team.team_name}`"
		:tabs="['student', 'parent', 'teacher']"
		@entity-click="handleEntityClick"
	></team-relations-modal>
	<upload-csv-modal v-model:show="uploadModal.show" :tab="props.tab" />
</template>

<script setup lang="ts">
import { reactive, watch } from "vue";
import usePaginate from "@/hooks/usePaginate";
import { computed } from "vue";
import pagination from "@/components/pagination.vue";
import downloadFile, { complexSearch } from "@/utils";
import type { TeamsType } from "@/api_types/school";
import EditTeamModal from "./editTeamModal.vue";
import AddTeamModal from "./addTeamModal.vue";
import TeamRelationsModal from "@/components/school/directory/RelationshipModal.vue";
import useTeams from "@/stores/school/teams";
import { useAlertsStore } from "@/stores/alerts";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import uploadCsvModal from "../uploadCsvModal.vue";
import FetchService from "@/services/fetch.service";
import RelationsDownload from "../relationsDownload.vue";
// import { useSchoolStore } from "@/stores/school";
import useTableSort from "@/hooks/useTableSort";
import { sort } from "@/utils/sort";
import SortHeader from "@/components/global/sortHeader.vue";
import { useAuthenticationStore } from "@/stores/authentication";

const props = defineProps<{
	tab: "teacher" | "parent" | "student" | "team";
	search: string;
	deleted: boolean;
	selectedEntity?: any;
}>();
const emit = defineEmits<{
  (e: 'update:search', ...args: any[]): void;
  (e: 'entity-click', entityData: any): void;
}>();

const { currentSort, setSort } = useTableSort([
	{
		key: "name",
		title: "name",
		sortKey: "name",
	},
]);

const authStore = useAuthenticationStore();
const canEditDirectory = computed(() => authStore.hasPolicy("school.directory.team.write"));

const state = reactive({
	loadPinned: [] as string[],
	loadDownload: false,
});

const editModal = reactive({ show: false, team: {} as TeamsType[number] });
const addModal = reactive({ show: false });
const editRelationModal = reactive({ show: false, team: {} as TeamsType[number] });

const uploadModal = reactive({
	show: false,
});
const teamsS = useTeams();
// const schoolStore = useSchoolStore();

teamsS.teams.reload();

const filteredData = computed(() => {
	return sort(
		teamsS.teams.data?.filter((i) => {
			if (i.deleted !== props.deleted) return false;
			return complexSearch(
				props.search || "",
				`${i.team_name} ${i.external_id} ${i.team_id} ${i.uid} ${i.teacher_description}`
			);
		}) || [],
		(i) => i.team_name,
		currentSort.value.asc
	);
});

const updatePinTeam = async (team: TeamsType[number]) => {
	state.loadPinned.push(team.uid);
	const res = await teamsS.updateTeam(team.uid, {
		pinned: !team.pinned,
		team_name: team.team_name,
	});
	useAlertsStore().addAlert(
		res ? "success" : "error",
		res ? "Team saved successfully" : "Failed to update team",
		5
	);
	state.loadPinned = state.loadPinned.filter((str) => str !== team.uid);
};

const { forComponent, paginatedResults } = usePaginate(filteredData);

const download = async () => {
	state.loadDownload = true;
	const res = await FetchService.fetchData("/teams/download", "get", undefined, true);
	const cType = res?.res?.headers.get?.("content-type") || "";
	if (typeof res?.data === "string" && cType.includes("text/csv")) {
		downloadFile(
			res.data,
			res?.res?.headers
				?.get("content-disposition")
				?.split("filename=")?.[1]
				?.replace(/"/g, "") || "teams.csv",
			"text/csv"
		);
	} else {
		useAlertsStore().addAlert(
			"error",
			"Failed to download teams, please try again later",
			10
		);
	}
	state.loadDownload = false;
};

// Handle entity clicks from relationship modal
const handleEntityClick = (entityData: any) => {
	emit('entity-click', entityData);
};

// Watch for selected entity changes and open the relationship modal
watch(() => props.selectedEntity, (newEntity) => {
	if (newEntity && newEntity.entityType === 'team') {
		// Find the team in the current data
		const team = teamsS.teams.data.find(t => t.uid === newEntity.entityId);
		if (team) {
			editRelationModal.team = team;
			editRelationModal.show = true;
		}
	}
}, { immediate: true });
</script>

<style scoped lang="scss">
.icon-col {
	width: 0px;
}
.table-row {
	td {
		transition: background-color 0.3s;
	}
	&.selected {
		td {
			background-color: #e6e6e689 !important;
		}
	}
}
</style>
